import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LoaderService } from 'src/app/loader.service';
import { SharedService } from 'src/app/shared/shared.service';
import { ToasterService } from 'src/app/toaster.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { Router, ActivatedRoute } from '@angular/router';
import { cityDisplayNameToShiftPrefix } from './utils';

@Component({
  selector: 'app-create-shift',
  templateUrl: './create-shift.component.html',
  styleUrls: ['./create-shift.component.css']
})
export class CreateShiftComponent implements OnInit {
  public shiftFormGroup = new FormGroup({
    city: new FormControl(null, [Validators.required]),
    mode: new FormControl(null, [Validators.required]),
    isDefault: new FormControl(false),
    shiftLabel: new FormControl(null, [Validators.required])
  });

  public cities: { id: string, name: string }[] = [];
  public modes: { id: string, name: string }[] = [];
  public finalShiftName: string = '';
  public shiftType: 'regular' | 'default' = 'regular';
  private returnTo: string = '/slabs';
  private cityIdFromQuery: string | null = null;
  private modeIdFromQuery: string | null = null;
  // validate mode once modes load
  private validateLaunchMode = false;

  get cityPrefix(): string {
    const city = this.shiftFormGroup.get('city').value;
    if (!city) return '';

    return city.name ? cityDisplayNameToShiftPrefix(city.name) : '';
  }

  get modePrefix(): string {
    const mode = this.shiftFormGroup.get('mode').value;
    if (!mode) return '';
    return mode.name ? mode.name.toUpperCase() : '';
  }

  constructor(
    private readonly sharedService: SharedService,
    private readonly loader: LoaderService,
    public readonly toasterService: ToasterService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit() {
    const params = this.route.snapshot.queryParams;
    if (params['returnTo']) {
      this.returnTo = params['returnTo'];
    }
    this.cityIdFromQuery = (params['city'] || '').trim() || null;
    this.modeIdFromQuery = (params['mode'] || '').trim() || null;
    this.validateLaunchMode = !!this.cityIdFromQuery;

    this.fetchCities();
    
    this.shiftFormGroup.get('city').valueChanges.subscribe(() => {
      this.shiftFormGroup.get('mode').setValue(null);
      this.updateFinalName();
      if (this.shiftFormGroup.get('city').value) {
        this.fetchModes(this.shiftFormGroup.get('city').value.id);
      }
    });

    this.shiftFormGroup.get('mode').valueChanges.subscribe(() => {
      this.updateFinalName();
    });

    this.shiftFormGroup.get('shiftLabel').valueChanges.subscribe((val: string) => {
      if (val && val !== val.toUpperCase()) {
        this.shiftFormGroup.get('shiftLabel').setValue(val.toUpperCase(), { emitEvent: false });
      }
      this.updateFinalName();
    });

  }

  public setShiftType(type: 'regular' | 'default'): void {
    this.shiftType = type;
    const shiftLabelControl = this.shiftFormGroup.get('shiftLabel');
    if (type === 'default') {
      this.shiftFormGroup.get('isDefault').setValue(true);
      shiftLabelControl.clearValidators();
      shiftLabelControl.setValue(null);
      shiftLabelControl.disable();
    } else {
      this.shiftFormGroup.get('isDefault').setValue(false);
      shiftLabelControl.setValidators([Validators.required]);
      shiftLabelControl.enable();
    }
    shiftLabelControl.updateValueAndValidity();
    this.updateFinalName();
  }

  private fetchCities(): void {
    this.loader.openLoading();
    this.sharedService.fetchCities()
      .toPromise()
      .then((res: any) => {
        this.cities = res.data.cities
          .filter(city => city.active)
          .map(city => ({
            id: city._id,
            name: city.displayName,
          }));
        
        // Pre-select city if cityId is provided in query params
        if (this.cityIdFromQuery && this.cities.length > 0) {
          const city = this.cities.find(c => c.id === this.cityIdFromQuery);
          if (city) {
            this.shiftFormGroup.get('city').setValue(city);
          }
        }
      })
      .catch(error => {
        this.toasterService.showToaster(new Toaster({ type: ToasterType.WARNING, message: 'Error fetching cities' }));
      })
      .finally(() => this.loader.closeLoading());
  }
  
  private fetchModes(cityId: string): void {
    if (!cityId) return;
    
    this.loader.openLoading();
    this.sharedService.fetchModes(cityId)
      .toPromise()
      .then((res: any) => {
        this.modes = res.data.modes || [];
        if (this.validateLaunchMode) {
          this.validateLaunchMode = false;
          if (!this.modeIdFromQuery) {
            this.toasterService.showToaster(new Toaster({
              type: ToasterType.WARNING,
              message: 'Mode is required in launch URL.',
            }));
          } else {
            const mode = this.modes.find(m => String(m.id) === String(this.modeIdFromQuery));
            if (mode) {
              this.shiftFormGroup.get('mode').setValue(mode);
            } else {
              this.toasterService.showToaster(new Toaster({
                type: ToasterType.WARNING,
                message: 'Invalid mode in launch URL for the selected city.',
              }));
            }
          }
          this.modeIdFromQuery = null;
        }
      })
      .catch(error => {
        console.error("Error fetching modes:", error);
        this.toasterService.showToaster(new Toaster({ 
          type: ToasterType.WARNING, 
          message: 'Error fetching modes' 
        }));
      })
      .finally(() => this.loader.closeLoading());
  }

  private updateFinalName(): void {
    const city = this.shiftFormGroup.get('city').value;
    const mode = this.shiftFormGroup.get('mode').value;
    const isDefault = this.shiftFormGroup.get('isDefault').value;
    const shiftLabel = this.shiftFormGroup.get('shiftLabel').value;

    if (city && mode) {
      const cityName = city.name ? cityDisplayNameToShiftPrefix(city.name) : '';
      const modeName = mode.name ? mode.name.toUpperCase() : '';

      if (isDefault) {
        this.finalShiftName = `${cityName}_${modeName}_DEFAULT`;
      } else if (shiftLabel) {
        const label = shiftLabel.toUpperCase().trim();
        this.finalShiftName = `${cityName}_${modeName}_${label}`;
      } else {
        this.finalShiftName = '';
      }
    } else {
      this.finalShiftName = '';
    }
  }

  public onSubmit(): void {
    if (this.shiftFormGroup.invalid) {
      this.toasterService.showToaster(new Toaster({ type: ToasterType.WARNING, message: 'Please fill all required fields' }));
      return;
    }

    const city = this.shiftFormGroup.get('city').value;
    const mode = this.shiftFormGroup.get('mode').value;
    const isDefault = this.shiftFormGroup.get('isDefault').value;
    const shiftLabel = this.shiftFormGroup.get('shiftLabel').value;

    if (!city || !mode || (!isDefault && !shiftLabel)) {
      this.toasterService.showToaster(new Toaster({ type: ToasterType.WARNING, message: 'Please fill all required fields' }));
      return;
    }

    this.loader.openLoading();
    this.sharedService.createShift({
      cities: [city.id],
      mode: mode.id,
      shiftName: this.finalShiftName,
      isDefault: isDefault
    })
      .toPromise()
      .then((res: any) => {
        this.toasterService.showToaster(new Toaster({ type: ToasterType.SUCCESS, message: 'Shift created successfully' }));
        this.router.navigate([this.returnTo]);
      })
      .catch(error => {
        let errorMessage = 'Failed to create shift';
        if (error && error.error) {
          const body = error.error;
          if (body.data && body.data.error) {
            if (body.data.error.message) {
              errorMessage = body.data.error.message;
            } else if (typeof body.data.error === 'string') {
              errorMessage = body.data.error;
            }
          } else if (body.message) {
            errorMessage = body.message;
          }
        } else if (error && error.message) {
          errorMessage = error.message;
        }
        this.toasterService.showToaster(new Toaster({ type: ToasterType.WARNING, message: errorMessage }));
      })
      .finally(() => this.loader.closeLoading());
  }

  public onCancel(): void {
    this.router.navigate([this.returnTo]);
  }
}
